/**
 * File Opening Utilities
 * 
 * Provides consistent file opening behavior across the app:
 * - Office files (docx, xlsx, pptx) → Open in Office 365 Online viewer
 * - PDFs → Open inline in browser
 * - Images → Open inline in browser  
 * - Web URLs → Open directly
 * - Other files → Download
 */

import { buildApiUrl } from '../config/apiConfig';

// File type categorization
const OFFICE_EXTENSIONS = new Set(['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'odt', 'ods', 'odp']);
const VIEWABLE_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'txt', 'json', 'csv', 'html']);

/**
 * Get file extension from filename or URL
 */
export function getFileExtension(filenameOrUrl: string): string {
  if (!filenameOrUrl) return '';
  // Remove query params and fragments
  const cleanPath = filenameOrUrl.split('?')[0].split('#')[0];
  const ext = cleanPath.toLowerCase().split('.').pop() || '';
  return ext;
}

/**
 * Check if file is an Office document
 */
export function isOfficeFile(filename: string): boolean {
  return OFFICE_EXTENSIONS.has(getFileExtension(filename));
}

/**
 * Check if file is viewable in browser (PDF, images, text)
 */
export function isViewableFile(filename: string): boolean {
  return VIEWABLE_EXTENSIONS.has(getFileExtension(filename));
}

/**
 * Check if URL is a blob proxy URL
 */
export function isBlobProxyUrl(url: string): boolean {
  return url.includes('/api/blob-proxy') || url.includes('blob-proxy');
}

/**
 * Check if URL is an internal blob storage URL
 */
export function isBlobStorageUrl(url: string): boolean {
  return url.includes('.blob.core.windows.net');
}

export type FileOpenMode = 'view' | 'download';

export interface FileOpenOptions {
  /** Force a specific mode (view or download). If not set, auto-detects based on file type. */
  mode?: FileOpenMode;
  /** Filename for download (used when URL doesn't indicate filename) */
  filename?: string;
}

/**
 * Get the appropriate URL for opening a file
 * 
 * @param url - The original file URL
 * @param filename - Optional filename (for file type detection)
 * @param options - Open options
 * @returns Modified URL with appropriate parameters
 */
export function getFileOpenUrl(url: string, filename?: string, options: FileOpenOptions = {}): string {
  if (!url) return '';
  
  const effectiveFilename = filename || url;
  const ext = getFileExtension(effectiveFilename);
  const isOffice = OFFICE_EXTENSIONS.has(ext);
  const isPdf = ext === 'pdf';
  const isViewable = VIEWABLE_EXTENSIONS.has(ext);
  
  // Handle /api/ paths - use buildApiUrl for dev proxy support
  let processedUrl = url;
  if (url.startsWith('/api/')) {
    processedUrl = buildApiUrl(url);
  }
  
  // For blob proxy URLs, add appropriate parameters
  if (isBlobProxyUrl(processedUrl)) {
    const forceDownload = options.mode === 'download';
    
    if (!forceDownload) {
      if (isOffice) {
        // Open in Office 365 Online viewer (proxy will generate direct SAS URL)
        processedUrl += (processedUrl.includes('?') ? '&' : '?') + 'view=1';
      } else if (isPdf || isViewable) {
        // Open inline in browser (PDFs, images, text)
        processedUrl += (processedUrl.includes('?') ? '&' : '?') + 'inline=1';
      }
    }
  }
  
  return processedUrl;
}

/**
 * Open a file URL with smart behavior based on file type
 * 
 * @param url - The file URL to open
 * @param filename - Optional filename for type detection
 * @param options - Open options
 */
export function openFile(url: string, filename?: string, options: FileOpenOptions = {}): void {
  if (!url) return;
  
  const processedUrl = getFileOpenUrl(url, filename, options);
  window.open(processedUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Download a file (forces download, no inline viewing)
 * 
 * @param url - The file URL to download
 * @param filename - Filename for the download
 */
export function downloadFile(url: string, filename?: string): void {
  if (!url) return;
  
  let processedUrl = url;
  if (url.startsWith('/api/')) {
    processedUrl = buildApiUrl(url);
  }
  
  // Create a temporary link to trigger download
  const link = document.createElement('a');
  link.href = processedUrl;
  if (filename) {
    link.download = filename;
  }
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Open file for viewing (prefers inline viewing when possible)
 * 
 * @param url - The file URL to view
 * @param filename - Optional filename for type detection
 */
export function viewFile(url: string, filename?: string): void {
  openFile(url, filename, { mode: 'view' });
}
